import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  listCustomerHistoryApi,
  fetchStaffGroupedDataApi,
  fetchGroupedCustomerDataApi,
  fetchStaffGroupedCountsApi,
  fetchLoginHistoryApi,
  fetchMonthlyReportApi,
  fetchUnpaidReportsApi,
  fetchPlanwiseReportApi,
} from "../services/GroupDataService";

// Thunk for listing customer history
export const listCustomerHistory = createAsyncThunk(
  "groupData/listCustomerHistory",
  async (filters = {}) => {
    const response = await listCustomerHistoryApi(filters);
    return response;
  }
);

// Thunk for fetching staff grouped data (collection amounts)
export const fetchStaffGroupedData = createAsyncThunk(
  "groupData/fetchStaffGroupedData",
  async () => {
    const response = await fetchStaffGroupedDataApi();
    return response;
  }
);

// Thunk for fetching grouped customer data (boxes)
export const fetchGroupedCustomerData = createAsyncThunk(
  "groupData/fetchGroupedCustomerData",
  async () => {
    const response = await fetchGroupedCustomerDataApi();
    return response;
  }
);

// Thunk for fetching staff grouped counts (box counts, paid/unpaid)
export const fetchStaffGroupedCounts = createAsyncThunk(
  "groupData/fetchStaffGroupedCounts",
  async () => {
    const response = await fetchStaffGroupedCountsApi();
    return response;
  }
);

// Thunk for fetching login history
export const fetchLoginHistory = createAsyncThunk(
  "groupData/fetchLoginHistory",
  async (filters = {}) => {
    const response = await fetchLoginHistoryApi(filters);
    return response;
  }
);

// Thunk for fetching monthly/yearly report
export const fetchMonthlyReport = createAsyncThunk(
  "groupData/fetchMonthlyReport",
  async () => {
    const response = await fetchMonthlyReportApi();
    return response;
  }
);

//// Thunk for fetching unpaid reports
export const fetchUnpaidReports = createAsyncThunk(
  "groupData/fetchUnpaidReports",
  async (filters = {}) => {
    const response = await fetchUnpaidReportsApi(filters);
    return response;
  }
);

///thunk for planwise report
export const fetchPlanwiseReport = createAsyncThunk(
  "groupData/fetchPlanwiseReport",
  async (filters = {}) => {
    const response = await fetchPlanwiseReportApi(filters);
    return response;
  }
);

const groupDataSlice = createSlice({
  name: "groupData",
  initialState: {
    history: [],
    staffGroupedData: {
      staff_data: [],
      user_data: [],
      month: "",
      date: "",
    },
    groupedCustomerData: {
      connected_boxes: 0,
      disconnected_boxes: 0,
      unpaid_boxes: 0,
      total_boxes: 0,
      connected_boxes_data: [],
      disconnected_boxes_data: [],
      unpaid_boxes_data: [],
      total_boxes_data: [],
    },
    staffGroupedCounts: {
      staff_grouped_counts: [],
      user_grouped_counts: [],
      month: "",
      date: "",
    },
    loginHistory: [],
    monthlyReport: [],
    unpaidReports: [],
    planwiseReportData: [],
    status: "idle",
    error: null,
  },
  reducers: {
    clearHistory: (state) => {
      state.history = [];
      state.status = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // Customer History
      .addCase(listCustomerHistory.pending, (state) => {
        state.status = "loading";
      })
      .addCase(listCustomerHistory.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.history = action.payload || [];
      })
      .addCase(listCustomerHistory.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      // Staff Grouped Data (Collection Amounts)
      .addCase(fetchStaffGroupedData.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchStaffGroupedData.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.staffGroupedData = action.payload || {
          staff_data: [],
          user_data: [],
          month: "",
          date: "",
        };
      })
      .addCase(fetchStaffGroupedData.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      // Grouped Customer Data (Boxes)
      .addCase(fetchGroupedCustomerData.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchGroupedCustomerData.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.groupedCustomerData = action.payload || {
          connected_boxes: 0,
          disconnected_boxes: 0,
          unpaid_boxes: 0,
          total_boxes: 0,
          connected_boxes_data: [],
          disconnected_boxes_data: [],
          unpaid_boxes_data: [],
          total_boxes_data: [],
        };
      })
      .addCase(fetchGroupedCustomerData.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      // Staff Grouped Counts (Box Counts, Paid/Unpaid)
      .addCase(fetchStaffGroupedCounts.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchStaffGroupedCounts.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.staffGroupedCounts = action.payload || {
          staff_grouped_counts: [],
          user_grouped_counts: [],
          month: "",
          date: "",
        };
      })
      .addCase(fetchStaffGroupedCounts.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      // Login History
      .addCase(fetchLoginHistory.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchLoginHistory.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.loginHistory = action.payload || [];
      })
      .addCase(fetchLoginHistory.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      // Monthly Report
      .addCase(fetchMonthlyReport.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchMonthlyReport.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.monthlyReport = action.payload || [];
      })
      .addCase(fetchMonthlyReport.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })

      // Planwise Report
      .addCase(fetchPlanwiseReport.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchPlanwiseReport.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.planwiseReportData = action.payload || [];
      })
      .addCase(fetchPlanwiseReport.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      });

    //Unpaid Reports
    builder
      .addCase(fetchUnpaidReports.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchUnpaidReports.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.unpaidReports = action.payload;
      })
      .addCase(fetchUnpaidReports.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      });
  },
});
export const { clearHistory } = groupDataSlice.actions;

export default groupDataSlice.reducer;
